import { Currency } from "@redotech/money/currencies";
import { IterableMap } from "@redotech/react-util/component";
import { useRequiredContext } from "@redotech/react-util/context";
import { useInput } from "@redotech/react-util/form";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import CartIcon from "@redotech/redo-icons/shopping-cart-03.svg";
import TrashIcon from "@redotech/redo-icons/trash.svg";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { ProgressBar } from "@redotech/redo-model/checkouts/checkout-optimization-settings";
import { THRESHOLD_UPSELL_WIDGET_AMOUNT_LEFT_PLACEHOLDER } from "@redotech/redo-model/checkouts/widget";
import {
  InsertionPosition,
  insertionPositionToDisplayText,
} from "@redotech/redo-model/widget";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoColorPickerBadge } from "@redotech/redo-web/arbiter-components/color-picker/redo-color-picker-badge";
import { LabelText } from "@redotech/redo-web/arbiter-components/common/descriptors";
import { sizeToDescriptorTextProps } from "@redotech/redo-web/arbiter-components/input/base-redo-text-input";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { CurrencySymbolIcon } from "@redotech/redo-web/currency-symbol-icon";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { groupInput, input, listInput } from "@redotech/ui/form";
import { safeParseEnum } from "@redotech/util/enum";
import { memo, useMemo, useState } from "react";
import * as css from "./cart-progress-bar-config-modal.module.css";
import { PreviewSection } from "./preview-section";

type SelectorPlacement = ProgressBar["selectors"][number];

const selectorInput = groupInput({
  selector: input<string>(),
  insertPosition: input<InsertionPosition>(),
});

const selectorsListInput = listInput(
  () => selectorInput,
  (): SelectorPlacement => ({
    selector: "",
    insertPosition: InsertionPosition.BEFORE,
  }),
  (_, index) => index,
);

const progressBarInput = groupInput({
  thresholdAmount: input<string>(),
  ltThresholdText: input<string>(),
  gteThresholdText: input<string>(),
  progressBarColor: input<string>(),
  selectors: selectorsListInput,
});

interface CartProgressBarConfigModalProps {
  progressBar: ProgressBar | undefined;
  treatmentName: string;
  onSave: (progressBar: ProgressBar | undefined) => void;
  onClose: () => void;
}

export const CartProgressBarConfigModal = memo(
  function CartProgressBarConfigModal({
    treatmentName,
    progressBar,
    onSave,
    onClose,
  }: CartProgressBarConfigModalProps) {
    const team = useRequiredContext(TeamContext);

    const shopCurrency =
      safeParseEnum(team._shopify?.currency, Currency) ?? Currency.USD;

    const [enabled, setEnabled] = useState(!!progressBar);

    const input = useInput(
      progressBarInput,
      progressBar
        ? {
            thresholdAmount: progressBar.threshold.amount.toString(),
            ltThresholdText: progressBar.ltThresholdText,
            gteThresholdText: progressBar.gteThresholdText,
            progressBarColor: progressBar.progressBarColor,
            selectors: progressBar.selectors,
          }
        : {
            thresholdAmount: "",
            ltThresholdText: "",
            gteThresholdText: "",
            progressBarColor: "#000000",
            selectors: [],
          },
    );

    const insertPositionOptions: RedoListItem<InsertionPosition>[] = useMemo(
      () =>
        Object.values(InsertionPosition).map((position) => ({
          id: position,
          type: "text",
          text: insertionPositionToDisplayText[position],
          value: position,
        })),
      [],
    );

    const handleSave = () => {
      if (!enabled) {
        onSave(undefined);
        return;
      }

      const thresholdAmount = parseFloat(input.value.thresholdAmount);
      if (isNaN(thresholdAmount) || thresholdAmount <= 0) {
        toast("Threshold amount must be greater than 0", { variant: "error" });
        return;
      }

      const progressBarData: ProgressBar = {
        threshold: { amount: thresholdAmount, currency: shopCurrency },
        ltThresholdText: input.value.ltThresholdText,
        gteThresholdText: input.value.gteThresholdText,
        progressBarColor: input.value.progressBarColor,
        selectors: input.value.selectors,
      };

      onSave(progressBarData);
    };

    return (
      <RedoModal
        headerIconAlignment="inline"
        modalSize="lg"
        noPadding
        onModalCloseRequested={onClose}
        primaryButton={{ text: "Save", onClick: handleSave }}
        secondaryButton={{ text: "Cancel", onClick: onClose }}
        subtitle="Configure the cart progress that shows shoppers how much they need to spend for free shipping"
        title={`${treatmentName} - Cart progress bar`}
        TitleIcon={CartIcon}
      >
        <Flex
          borderBottomWidth="1px"
          borderColor="primary"
          borderStyle="solid"
          borderTopWidth="1px"
          dir="row"
          gap="none"
          w="full"
        >
          <PreviewSection
            progressBarConfig={
              enabled &&
              input.value.thresholdAmount &&
              parseFloat(input.value.thresholdAmount) > 0
                ? {
                    threshold: {
                      amount: parseFloat(input.value.thresholdAmount),
                      currency: shopCurrency,
                    },
                    ltThresholdText: input.value.ltThresholdText,
                    gteThresholdText: input.value.gteThresholdText,
                    progressBarColor: input.value.progressBarColor,
                  }
                : undefined
            }
            shopCurrency={shopCurrency}
          />
          <Flex
            bgColor="secondary"
            borderColor="primary"
            borderLeftWidth="1px"
            borderStyle="solid"
            className={css.configurationSection}
            dir="column"
            gap="3xl"
            p="3xl"
          >
            <Text fontSize="md" fontWeight="semibold" textColor="primary">
              Widget settings
            </Text>
            <Flex dir="column" gap="xl">
              <RedoToggle
                label="Enable widget"
                setValue={setEnabled}
                size="sm"
                value={enabled}
              />
              {enabled && (
                <RedoTextInput
                  description='Customers must reach this amount to see the "threshold met" message'
                  fullWidth
                  IconLeading={() => (
                    <CurrencySymbolIcon currency={shopCurrency} />
                  )}
                  inputTypeHint="number"
                  label="Threshold amount"
                  min={0}
                  setValue={input.inputs.thresholdAmount.setValue}
                  size="sm"
                  value={input.inputs.thresholdAmount.value}
                />
              )}
            </Flex>
            {enabled && (
              <>
                <Text fontSize="md" fontWeight="semibold" textColor="primary">
                  Title
                </Text>
                <Flex dir="column" gap="xl">
                  <RedoTextInput
                    description={`Add ${THRESHOLD_UPSELL_WIDGET_AMOUNT_LEFT_PLACEHOLDER} to show remaining amount.`}
                    fullWidth
                    label="Title when threshold is not met"
                    setValue={input.inputs.ltThresholdText.setValue}
                    size="sm"
                    value={input.inputs.ltThresholdText.value}
                  />
                  <RedoTextInput
                    fullWidth
                    label="Title when threshold is met"
                    setValue={input.inputs.gteThresholdText.setValue}
                    size="sm"
                    value={input.inputs.gteThresholdText.value}
                  />
                </Flex>
                <Text fontSize="md" fontWeight="semibold" textColor="primary">
                  Style
                </Text>
                <Flex dir="column" gap="sm">
                  <LabelText
                    label="Color"
                    size={sizeToDescriptorTextProps.sm}
                  />
                  <RedoColorPickerBadge
                    disabled={!enabled}
                    onChange={input.inputs.progressBarColor.setValue}
                    value={input.inputs.progressBarColor.value}
                  />
                </Flex>
                <Text fontSize="md" fontWeight="semibold" textColor="primary">
                  Placement
                </Text>
                <IterableMap
                  items={input.inputs.selectors.inputs}
                  keyFn={(_, i) => i}
                >
                  {(selectorInput, i) => (
                    <Flex dir="column" gap="md" key={i} w="full">
                      <Flex align="center" dir="row" gap="md" w="full">
                        <Flex flex={1}>
                          <RedoTextInput
                            description="CSS selector for placement"
                            fullWidth
                            label="Selector"
                            setValue={selectorInput.inputs.selector.setValue}
                            size="sm"
                            value={selectorInput.inputs.selector.value}
                          />
                        </Flex>
                        <RedoButton
                          hierarchy="tertiary"
                          IconLeading={TrashIcon}
                          onClick={() =>
                            input.inputs.selectors.setValue(
                              input.inputs.selectors.value.filter(
                                (_, index) => index !== i,
                              ),
                            )
                          }
                          size="sm"
                          theme="destructive"
                        />
                      </Flex>
                      <RedoSingleSelectDropdownInput<InsertionPosition>
                        label="Insert position"
                        options={insertPositionOptions}
                        optionSelected={({ item }) => {
                          selectorInput.inputs.insertPosition.setValue(
                            item.value,
                          );
                        }}
                        selectedItem={insertPositionOptions.find(
                          (o) =>
                            o.value ===
                            selectorInput.inputs.insertPosition.value,
                        )}
                        size="xs"
                      />
                    </Flex>
                  )}
                </IterableMap>
                <RedoButton
                  hierarchy="secondary"
                  IconLeading={PlusIcon}
                  onClick={() =>
                    input.inputs.selectors.setValue([
                      ...input.inputs.selectors.value,
                      {
                        selector: "",
                        insertPosition: InsertionPosition.BEFORE,
                      },
                    ])
                  }
                  size="sm"
                  text="Add placement"
                />
              </>
            )}
          </Flex>
        </Flex>
      </RedoModal>
    );
  },
);
